import { useRef, useState, type ChangeEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateInventoryItem } from "../../api/inventory";
import { Alert } from "../../components/ui/Alert";
import BackButton from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { FilterBar } from "../../components/ui/FilterBar";
import { Form } from "../../components/ui/Form";
import { Field } from "../../components/ui/Field";
import { Page } from "../../components/ui/Page";

import { UserSelect } from "../../components/ui/UserSelect";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import type { InventoryAudience } from "../../types/inventory";
import type { UserLightResponse } from "../../types/user";
import { formatBytes } from "../../utils/file";
import { hasTrimmedValue, isOptionalHttpUrl } from "../../utils/formValidation";
import styles from "./NewInventoryPage.module.css";

interface InventoryFormValues {
  name: string;
  description: string;
  improvementNotes: string;
  githubUrl: string;
  deploymentLocation: string;
  audience: InventoryAudience;
  participants: UserLightResponse[];
}

export default function NewInventoryPage() {
  const { t } = useTranslation();
  useLayoutTitle(t("inventory.newItem"));
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const createItem = useCreateInventoryItem();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<InventoryFormValues>({
    defaultValues: {
      name: "",
      description: "",
      improvementNotes: "",
      githubUrl: "",
      deploymentLocation: "",
      audience: "internal",
      participants: [],
    },
  });

  const audience = watch("audience");

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      e.target.value = "";
      setPendingFiles((prev) => [...prev, ...newFiles]);
    }
  }

  function removeFile(index: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  const onSubmit = handleSubmit((values) => {
    createItem.mutate(
      {
        name: values.name.trim(),
        description: values.description.trim(),
        audience: values.audience,
        participantIds: values.participants.map((x) => x.id),
        ...(values.improvementNotes.trim() ? { improvementNotes: values.improvementNotes.trim() } : {}),
        ...(values.githubUrl.trim() ? { githubUrl: values.githubUrl.trim() } : {}),
        ...(values.deploymentLocation.trim() ? { deploymentLocation: values.deploymentLocation.trim() } : {}),
        ...(pendingFiles.length > 0 ? { attachments: pendingFiles } : {}),
      },
      {
        onSuccess: (res) => {
          navigate(`/inventory/${res.id}`);
        },
      },
    );
  });

  return (
    <Page width="wide">
      <Card>
        <BackButton
          label={t("inventory.backToInventories")}
          onClick={() => navigate("/inventory")}
          className={styles.backButton}
        />
        <Form noValidate onSubmit={onSubmit}>
          <Field.Input
            label={t("inventory.name")}
            error={errors.name?.message}
            {...register("name", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />
          <Field.Textarea
            label={t("inventory.description")}
            info={t("common.descriptionHint")}
            infoTitle={t("inventory.description")}
            error={errors.description?.message}
            {...register("description", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />

          <Field.Textarea
            label={`${t("inventory.improvementNotes")} (${t("common.optional")})`}
            info={t("inventory.improvementNotesInfo")}
            infoTitle={t("inventory.improvementNotes")}
            {...register("improvementNotes")}
          />
          <div className={styles.inlineGrid}>
            <Field.Input
              label={`${t("inventory.githubUrl")} (${t("common.optional")})`}
              type="url"
              error={errors.githubUrl?.message}
              {...register("githubUrl", {
                validate: (value) => isOptionalHttpUrl(value) || t("common.invalidUrl"),
              })}
            />
            <Field.Input
              label={`${t("inventory.deploymentLocation")} (${t("common.optional")})`}
              info={t("inventory.deploymentLocationInfo")}
              infoTitle={t("inventory.deploymentLocation")}
              {...register("deploymentLocation")}
            />
          </div>

          <Form.Field
            label={t("inventory.audience")}
            infoTitle={t("inventory.audience")}
            info={
              <>
                <p><strong>{t("inventory.audienceInternal")}</strong> - {t("requests.audienceInternalInfo")}</p>
                <p><strong>{t("inventory.audienceExternal")}</strong> - {t("requests.audienceExternalInfo")}</p>
              </>
            }
          >
            <Controller
              name="audience"
              control={control}
              render={({ field }) => (
                <FilterBar
                  value={field.value}
                  options={[
                    { value: "internal", label: t("inventory.audienceInternal") },
                    { value: "external", label: t("inventory.audienceExternal") },
                  ]}
                  onChange={field.onChange}
                />
              )}
            />
          </Form.Field>
          {audience === "external" && <Alert>{t("inventory.audienceExternalAlert")}</Alert>}
          <Controller
            name="participants"
            control={control}
            render={({ field }) => (
              <UserSelect
                label={t("inventory.participants")}
                selected={field.value}
                onChange={field.onChange}
                excludeIds={currentUser ? [currentUser.id] : []}
              />
            )}
          />

          <Form.Field label={`${t("inventory.attachments")} (${t("common.optional")})`}>
            <div>
              <input
                ref={fileRef}
                type="file"
                multiple
                className={styles.hiddenInput}
                onChange={handleFileChange}
              />
              <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                {t("inventory.addAttachment")}
              </Button>
            </div>
            {pendingFiles.length > 0 && (
              <ul className={styles.attachmentList}>
                {pendingFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`} className={styles.attachmentItem}>
                    <div>
                      <p className={styles.attachmentName}>{file.name}</p>
                      <p className={styles.attachmentMeta}>{formatBytes(file.size)}</p>
                    </div>
                    <Button type="button" variant="danger" size="sm" onClick={() => removeFile(index)}>
                      {t("common.remove")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Form.Field>

          {createItem.isError && <Form.Feedback type="error">{t("common.error")}</Form.Feedback>}

          <Form.Actions>
            <Button type="button" variant="secondary" onClick={() => navigate("/inventory")}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={createItem.isPending}>
              {t("common.submit")}
            </Button>
          </Form.Actions>
        </Form>
      </Card>
    </Page>
  );
}
